import { SHARE_ASSETS, type ShareAssetImages } from './shareAssets';

let imagesPromise: Promise<ShareAssetImages> | undefined;
let qrPromise: Promise<string> | undefined;
const blobDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result));
  reader.onerror = () => reject(new Error('图片素材读取失败'));
  reader.readAsDataURL(blob);
});

/** Embed same-origin assets so the exported SVG never depends on external image requests. */
export function loadShareImages() {
  if (!imagesPromise) imagesPromise = Promise.all(Object.entries(SHARE_ASSETS).map(async ([key, url]) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error('图片素材加载失败');
    const uri = await blobDataUrl(await response.blob());
    return [key, uri];
  })).then(entries => Object.fromEntries(entries) as ShareAssetImages).catch(error => {
    imagesPromise = undefined;
    throw error;
  });
  return imagesPromise;
}

/** The receipt only needs a QR: a photo failure must not block sharing completed training. */
export function loadCompletionQR() {
  if (!qrPromise) qrPromise = fetch(SHARE_ASSETS.qr).then(async response => {
    if (!response.ok) throw new Error('二维码加载失败');
    return blobDataUrl(await response.blob());
  }).catch(error => { qrPromise = undefined; throw error; });
  return qrPromise;
}

export async function exportSharePoster(svg: SVGSVGElement): Promise<Blob> {
  const source = svg.cloneNode(true) as SVGSVGElement;
  source.setAttribute('width', '770'); source.setAttribute('height', '1228');
  source.removeAttribute('class');
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(source)], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve(); image.onerror = () => reject(new Error('分享图片生成失败')); image.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = 1540; canvas.height = 2456;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('当前浏览器无法生成图片');
    context.fillStyle = '#f0f7ff'; context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('分享图片生成失败')), 'image/png'));
  } finally { URL.revokeObjectURL(url); }
}

export interface ImageShareAPI {
  share?: (data: ShareData) => Promise<void>;
  canShare?: (data: ShareData) => boolean;
}
/** The caller prepares the file before the click, preserving browser user activation. */
export async function sharePreparedImage(file: File, api: ImageShareAPI = navigator) {
  try {
    if (!api.share || !api.canShare?.({ files: [file] })) return 'download' as const;
    await api.share({ files: [file], title: '练刻 · 训练分享' });
    return 'shared' as const;
  } catch (error) {
    return error instanceof Error && error.name === 'AbortError' ? 'cancelled' as const : 'download' as const;
  }
}

export function downloadShareImage(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url; link.download = fileName;
  document.body.append(link); link.click(); link.remove();
}
